import { useEffect, useRef, useState, startTransition, ViewTransition } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { DEFAULT_SOUND, type NotificationSound } from '@avslack/shared';
import { onReminderDue, type DueReminder } from '../lib/reminder-bus';
import { pushDeliversHere } from '../lib/push-device';
import { playNotificationSound } from '../lib/sound';
import { useNotificationPrefs } from '../data/notifications';
import { useAckReminder } from '../data/reminders';
import { useUsers } from '../data/users';
import { Icon } from './Icon';

/**
 * Lejart emlekezteto - felugro ablak.
 *
 * ─── Miert nem a szokasos ertesites? ────────────────────────────────────────
 *
 * Mert ez ATTORI a nemitast. A rendszer minden mas ertesitese a beallitasokat
 * koveti; ez az egy nem. Aki emlekeztetot ker, azt eppen az erdekli, hogy
 * akkor is eszrevegye, ha csendre allitotta magat.
 *
 * Ezert nem is tunik el magatol: a felhasznalonak kell nyugtaznia. Egy
 * elhalvanyulo buborek pont annyit erne, mint a semmi - lehet, hogy epp nem
 * nezte a kepernyot.
 */
/**
 * A rendszer-ertesites es a hang.
 *
 * ─── Miert a service workeren at? ───────────────────────────────────────────
 *
 * A `new Notification(...)` a LAPHOZ tartozik: ha a bongeszo felfuggeszti a
 * hatterben levo fület, elmaradhat. A service worker regisztraciojan keresztul
 * kuldott ertesites fuggetlen a laptol, es tamogatja a
 * `requireInteraction`-t - vagyis nem tunik el magatol.
 *
 * ─── Ki adja a hangot? ──────────────────────────────────────────────────────
 *
 * Ha a lap LATSZIK, mi szolaltatjuk meg a sajat hangunkat, es a rendszer
 * ertesitese nema marad - kulonben ket hang szolna egymasra.
 *
 * Ha a lap a HATTERBEN van, forditva: a rendszer hangjara bizzuk. A hatterbe
 * kerult lap hangjat a bongeszo visszafoghatja, es eppen ilyenkor szamit a
 * legjobban, hogy hallhato legyen.
 *
 * ─── Melyik hang? ───────────────────────────────────────────────────────────
 *
 * A felhasznalo VALASZTOTT hangja, es nem az alapertelmezes. Korabban itt fix
 * `DEFAULT_SOUND` allt: aki a beallitasokban kifejezetten "nincs"-et valasztott
 * - vagyis azt kerte, hogy ne szoljon semmi -, annal az emlekezteto megis
 * megszolalt, raadasul egy olyan hanggal, amit sosem valasztott.
 *
 * A NEMITAST ez tovabbra is attori (lasd fent): a rendszer csendre allitasa
 * nem ugyanaz, mint az alkalmazasban kikapcsolni a hangot. Az elso egy
 * helyzetrol szol, a masodik egy dontesrol.
 */
async function announce(reminder: DueReminder, sound: NotificationSound): Promise<void> {
  const visible = typeof document !== 'undefined' && document.visibilityState === 'visible';

  if (visible) playNotificationSound(sound);

  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  /*
    A szerver az emlekeztetorol MINDIG kuld push-t is. Ahol az megerkezik,
    ott a mienk csak a masodik peldany lenne ugyanarrol.
  */
  if (pushDeliversHere()) return;

  const options: NotificationOptions & { requireInteraction?: boolean } = {
    body: reminder.text,
    tag: reminder.reminderId,
    icon: '/icons/icon-192.png',
    badge: '/icons/badge-72.png',
    silent: visible,
    requireInteraction: true,
  };

  try {
    const registration = await navigator.serviceWorker?.ready;
    if (registration) {
      await registration.showNotification('Emlékeztető', options);
      return;
    }
  } catch {
    // A service worker hianya nem indok arra, hogy ne jelezzunk sehogy.
  }

  try {
    new Notification('Emlékeztető', options);
  } catch {
    // Az ertesites hianya nem torheti el a felugro ablakot.
  }
}

export function ReminderPopup() {
  const [queue, setQueue] = useState<DueReminder[]>([]);
  const { nameOf } = useUsers();
  const ack = useAckReminder();
  const navigate = useNavigate();

  /*
    A beallitas REFBEN, nem fuggosegkent.

    A feliratkozas egyszer fut le (ures fuggoseglista), hogy egy be nem
    erkezett beallitas ne szakitsa le es kosse ujra a busz-figyelot - kozben
    egy epp lejaro emlekezteto elveszne.
  */
  const { data: prefs } = useNotificationPrefs();
  const soundRef = useRef<NotificationSound>(DEFAULT_SOUND);
  soundRef.current = prefs?.soundName ?? DEFAULT_SOUND;

  useEffect(
    () =>
      onReminderDue((reminder) => {
        setQueue((current) =>
          // Ugyanaz az emlekezteto ne kerulhessen be ketszer.
          current.some((item) => item.reminderId === reminder.reminderId)
            ? current
            : [...current, reminder],
        );

        void announce(reminder, soundRef.current);
      }),
    [],
  );

  const current = queue[0];
  if (!current) return null;

  /*
    Atmenetkent: a lezart doboz lecsuszik, es ha van meg a sorban, a kovetkezo
    a sajat animaciojaval erkezik a helyere - latszik, hogy ez EGY MASIK.
  */
  const dismiss = () => startTransition(() => setQueue((items) => items.slice(1)));

  return (
    <ViewTransition key={current.deliveryId} exit="popup-out" default="none">
      <div className="animate-fade-rise fixed bottom-4 right-4 z-[60] w-80 rounded-card border border-accent/40 bg-surface-raised p-4 shadow-modal">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 text-accent">
            <Icon name="ora" />
          </span>
          {/*
          A role="alert" CSAK a szovegen: a "meg N var" valtozasa ne olvastassa
          fel ujra, a gombok se keruljenek bele. A FOKUSZT nem vesszuk el -
          munka kozben ugrik fel.
        */}
          <div role="alert" className="min-w-0 flex-1">
            <p className="font-display text-sm font-semibold">Emlékeztető</p>
            <p className="mt-1 whitespace-pre-wrap break-words text-sm text-ink">{current.text}</p>
            <p className="mt-2 text-2xs text-ink-faint">
              {nameOf(current.creatorId)} ·{' '}
              {new Date(current.remindAt).toLocaleString('hu-HU', {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </p>
          </div>
        </div>

        {/*
        KET valasz, mert ket kulonbozo dolog.

        A "Megvan" azt mondja, hogy elintezted: az elozmenyben zold pipa
        kerul melle, es nem marad ott nyitottkent. A "Később" csak eltunteti
        az ablakot - a teendo megmarad, es a lista tovabbra is szamon tartja.

        Ha csak egy gomb lenne, valamelyik jelentes elveszne: vagy nem
        lehetne felretenni, vagy a kipipalas jelentene semmit.
      */}
        <div className="mt-3 flex items-center justify-between gap-2">
          {/* Ha tobb gyult ossze, latszodjon, hany var meg. */}
          <span className="text-2xs text-ink-faint">
            {queue.length > 1 ? `még ${queue.length - 1} vár` : ''}
          </span>
          <div className="flex items-center gap-1.5">
            {/* Mentett elem emlekeztetoje: egy kattintas az uzenetig (U7). */}
            {current.link && (
              <button
                type="button"
                onClick={() => {
                  const { channelId, messageId } = current.link!;
                  void navigate({
                    to: '/c/$channelId',
                    params: { channelId },
                    search: { m: messageId },
                  });
                  dismiss();
                }}
                className="rounded-control px-2.5 py-1.5 text-sm text-accent transition-colors hover:bg-surface-hover"
              >
                Megnyitás
              </button>
            )}
            <button
              type="button"
              onClick={dismiss}
              className="rounded-control px-2.5 py-1.5 text-sm text-ink-muted transition-colors hover:bg-surface-hover hover:text-ink"
            >
              Később
            </button>
            <button
              type="button"
              onClick={() => {
                /*
                A KIPIPALAS nem varat meg.

                Az ablak azonnal eltunik; ha a halozat epp akad, a nyugtazas
                kesobb is megerkezik. Egy porgo gomb egy emlekezteton csak
                azt erne el, hogy a felhasznalo ott ragadjon.
              */
                ack.mutate({ deliveryId: current.deliveryId, acked: true });
                dismiss();
              }}
              className="flex items-center gap-1.5 rounded-control bg-accent px-3 py-1.5 text-sm font-semibold text-accent-ink transition-colors duration-(--duration-quick) hover:bg-accent-hover"
            >
              <Icon name="pipa" size="sm" />
              Megvan
            </button>
          </div>
        </div>
      </div>
    </ViewTransition>
  );
}
